import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "trading-chart-container__content" }

import { computed, onBeforeUnmount, onMounted, ref, shallowRef, toRefs, watch } from 'vue';

import { authService } from '@exchange/libs/auth/service/src';
import { priceChartService, TV_CHART_CONTAINER as chartContainerId } from '@exchange/libs/charting/service/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import useColorTheme from '@exchange/libs/composables/shared/src/lib/useTheme';
import { marketService, MarketState } from '@exchange/libs/market/service/src';
import { modalService } from '@exchange/libs/modals/src';
import MarketOverlay from '@exchange/libs/overlays/MarketOverlay.vue';
import { langsInfoService } from '@exchange/libs/utils/langs-info/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import PriceChart from './PriceChart.vue';
import PriceChartBanner from './PriceChartBanner.vue';
import ResetColorChangeModal from './ResetColorChangeModal.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradingChartContainer',
  props: {
  marketId: { type: String, required: true },
},
  setup(__props) {

const props = __props;

const { marketId } = toRefs(props);

const { isXS } = useMQBreakpoints();
const { currentTheme } = useColorTheme();

const { getMarket, getMarketName, getMarketState } = marketService;

const market = computed(() => getMarket(marketId.value));
const marketState = computed(() => getMarketState(market.value));
const marketName = computed(() => getMarketName(market.value));
const showChartOverlay = computed(() => marketState.value && [MarketState.NEW, MarketState.POST_ONLY].includes(marketState.value));

const useChartKey = () => {
  const chartKey = ref(new Date().getTime());
  const hasBeenAuthenticated = computed(() => authService.initialLoadAuthIsChecked && authService.isAuthenticated);

  watch(
    hasBeenAuthenticated,
    (value) => {
      if (value) {
        chartKey.value = new Date().getTime();
      }
    },
    { immediate: true },
  );

  return {
    chartKey,
    hasBeenAuthenticated,
  };
};
const { chartKey, hasBeenAuthenticated } = useChartKey();

const { createWidget, removeWidget, setSymbol, savedDrawingsInfo, widgetIsReady, dataIsLoading } = priceChartService;

const { language } = langsInfoService;

const isSaveChartLayoutsFailedBannerClosed = ref(true);

const chartLoadingNoOpacity = computed(() => !widgetIsReady.value);
const loading = computed(() => dataIsLoading.value[marketId.value]);
const showLoading = computed(() => loading.value || chartLoadingNoOpacity.value || !authService.initialLoadAuthIsChecked);
const okToShowSaveChartLayoutsFailedBanner = computed(() => hasBeenAuthenticated.value && savedDrawingsInfo.restorationComplete && !savedDrawingsInfo.restoredSuccessfully);
const showSaveChartLayoutsFailedBanner = computed(() => okToShowSaveChartLayoutsFailedBanner.value && isSaveChartLayoutsFailedBannerClosed.value === false);

watch(okToShowSaveChartLayoutsFailedBanner, (value) => {
  if (value) {
    isSaveChartLayoutsFailedBannerClosed.value = false;
  }
});

const getWidgetColorTheme = () => (currentTheme.value === 'dark' ? 'Dark' : 'Light');

const createChartWidget = async (mId: string) => {
  try {
    await createWidget({
      marketId: mId,
      isXSmallScreen: isXS.value,
      theme: getWidgetColorTheme(),
    });
  } catch (e) {
    logger.warn('Widget creation failed', e);
    throw e;
  }
};

watch(marketId, (nValue, oValue) => {
  if (!oValue) {
    createChartWidget(nValue);
  } else {
    setSymbol(nValue, isXS.value, getWidgetColorTheme());
  }
});
watch(language, (o, n) => {
  if (o !== n) {
    createChartWidget(marketId.value);
  }
});

const onSaveChartLayoutsFailedBannerRefresh = () => {
  createChartWidget(marketId.value);
};
const onSaveChartLayoutsFailedBannerClose = () => {
  isSaveChartLayoutsFailedBannerClosed.value = true;
};

const dialWithResettingColors = (doRequiredUpdates: ({ solicitedOnly }: { solicitedOnly: boolean }) => Promise<void>) =>
  new Promise<void>((resolve) => {
    const resetModal = modalService.show(
      shallowRef(ResetColorChangeModal),
      {},
      {
        'please-reset': async () => {
          await doRequiredUpdates({ solicitedOnly: true });
          modalService.hide(resetModal);

          onSaveChartLayoutsFailedBannerRefresh();
          resolve();
        },
        'do-no-reset': async () => {
          await doRequiredUpdates({ solicitedOnly: false });
          modalService.hide(resetModal);
          resolve();
        },
      },
    );
  });

onMounted(() => {
  priceChartService.dialWithResettingDrawingColors = dialWithResettingColors;
  createChartWidget(marketId.value);
});

onBeforeUnmount(() => {
  priceChartService.dialWithResettingDrawingColors = undefined;
  removeWidget();
});

return (_ctx: any,_cache: any) => {
  const _component_ot_transition_fade = _resolveComponent("ot-transition-fade")!
  const _component_ot_module_container = _resolveComponent("ot-module-container")!

  return (_openBlock(), _createBlock(_component_ot_module_container, {
    class: "trading-chart-container",
    loading: !showChartOverlay.value && showLoading.value,
    "loading-no-opacity": chartLoadingNoOpacity.value
  }, {
    "main-content": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (showChartOverlay.value)
          ? (_openBlock(), _createBlock(_component_ot_transition_fade, { key: 0 }, {
              default: _withCtx(() => [
                _createVNode(MarketOverlay, {
                  "market-name": marketName.value,
                  "market-state": marketState.value,
                  "has-been-authenticated": _unref(hasBeenAuthenticated)
                }, null, 8, ["market-name", "market-state", "has-been-authenticated"])
              ]),
              _: 1
            }))
          : (showSaveChartLayoutsFailedBanner.value)
            ? (_openBlock(), _createBlock(PriceChartBanner, {
                key: 1,
                class: "trading-chart-container__banner",
                onRefresh: onSaveChartLayoutsFailedBannerRefresh,
                onClose: onSaveChartLayoutsFailedBannerClose
              }))
            : _createCommentVNode("", true),
        (_unref(marketId))
          ? (_openBlock(), _createBlock(PriceChart, {
              key: _unref(chartKey),
              class: "trading-chart-container__chart",
              "container-id": _unref(chartContainerId)
            }, null, 8, ["container-id"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["loading", "loading-no-opacity"]))
}
}

})